'use client';

import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  CheckBadgeIcon,
  LockClosedIcon,
  ServerIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import Link from 'next/link';

interface TrustBadgesModalProps {
  isOpen: boolean;
  onClose: () => void;
  darkMode?: boolean;
}

export default function TrustBadgesModal({
  isOpen,
  onClose,
  darkMode = false,
}: TrustBadgesModalProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`w-full max-w-2xl transform overflow-hidden rounded-lg shadow-xl backdrop-blur-md transition-all ${
                  darkMode ? 'bg-gray-800/75' : 'bg-white/75'
                }`}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4">
                  <Dialog.Title
                    className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}
                  >
                    Your Trust is Our Priority
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className={`rounded-lg p-2 transition-colors ${
                      darkMode
                        ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="px-6 py-6">
                  {/* Trust Badges Grid */}
                  <div className="mb-6 grid grid-cols-2 gap-4">
                    {/* We never sell your data */}
                    <Link
                      href="/trust-center/security"
                      onClick={onClose}
                      className={`cursor-pointer rounded-lg border p-4 transition-colors ${darkMode ? 'border-gray-600 bg-gray-700/50 hover:border-indigo-500' : 'border-gray-200 bg-gray-50 hover:border-indigo-500'}`}
                    >
                      <div className="flex items-center gap-3">
                        <ShieldCheckIcon
                          className={`h-8 w-8 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}
                        />
                        <div>
                          <h3
                            className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}
                          >
                            We never sell your data
                          </h3>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            - Ever!
                          </p>
                        </div>
                      </div>
                    </Link>

                    {/* EU Hosting */}
                    <Link
                      href="/trust-center/security"
                      onClick={onClose}
                      className={`cursor-pointer rounded-lg border p-4 transition-colors ${darkMode ? 'border-gray-600 bg-gray-700/50 hover:border-indigo-500' : 'border-gray-200 bg-gray-50 hover:border-indigo-500'}`}
                    >
                      <div className="flex items-center gap-3">
                        <GlobeAltIcon
                          className={`h-8 w-8 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}
                        />
                        <div>
                          <h3
                            className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}
                          >
                            EU Hosting
                          </h3>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Data stored in EU
                          </p>
                        </div>
                      </div>
                    </Link>

                    {/* GDPR Compliant */}
                    <Link
                      href="/trust-center/security"
                      onClick={onClose}
                      className={`cursor-pointer rounded-lg border p-4 transition-colors ${darkMode ? 'border-gray-600 bg-gray-700/50 hover:border-indigo-500' : 'border-gray-200 bg-gray-50 hover:border-indigo-500'}`}
                    >
                      <div className="flex items-center gap-3">
                        <CheckBadgeIcon
                          className={`h-8 w-8 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}
                        />
                        <div>
                          <h3
                            className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}
                          >
                            GDPR Compliant
                          </h3>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Full compliance
                          </p>
                        </div>
                      </div>
                    </Link>

                    {/* AES-256 Encryption */}
                    <Link
                      href="/trust-center/security"
                      onClick={onClose}
                      className={`cursor-pointer rounded-lg border p-4 transition-colors ${darkMode ? 'border-gray-600 bg-gray-700/50 hover:border-indigo-500' : 'border-gray-200 bg-gray-50 hover:border-indigo-500'}`}
                    >
                      <div className="flex items-center gap-3">
                        <LockClosedIcon
                          className={`h-8 w-8 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}
                        />
                        <div>
                          <h3
                            className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}
                          >
                            AES-256 Encryption
                          </h3>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Bank-level security
                          </p>
                        </div>
                      </div>
                    </Link>

                    {/* Enterprise SSO */}
                    <Link
                      href="/trust-center/security"
                      onClick={onClose}
                      className={`cursor-pointer rounded-lg border p-4 transition-colors ${darkMode ? 'border-gray-600 bg-gray-700/50 hover:border-indigo-500' : 'border-gray-200 bg-gray-50 hover:border-indigo-500'}`}
                    >
                      <div className="flex items-center gap-3">
                        <KeyIcon
                          className={`h-8 w-8 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}
                        />
                        <div>
                          <h3
                            className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}
                          >
                            Enterprise SSO
                          </h3>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            SAML & OIDC
                          </p>
                        </div>
                      </div>
                    </Link>

                    {/* ISO 27001 */}
                    <Link
                      href="/trust-center/security"
                      onClick={onClose}
                      className={`cursor-pointer rounded-lg border p-4 transition-colors ${darkMode ? 'border-gray-600 bg-gray-700/50 hover:border-indigo-500' : 'border-gray-200 bg-gray-50 hover:border-indigo-500'}`}
                    >
                      <div className="flex items-center gap-3">
                        <ServerIcon
                          className={`h-8 w-8 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}
                        />
                        <div>
                          <h3
                            className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}
                          >
                            ISO 27001
                          </h3>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Infrastructure certified
                          </p>
                        </div>
                      </div>
                    </Link>
                  </div>

                  {/* Description */}
                  <p className={`mb-6 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Your data security and privacy are our top priorities. We follow industry best
                    practices and comply with international standards to keep your information safe.
                  </p>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={onClose}
                      className={`flex-1 rounded-lg px-4 py-3 font-medium transition-colors ${
                        darkMode
                          ? 'bg-gray-700 text-white hover:bg-gray-600'
                          : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                      }`}
                    >
                      Close
                    </button>
                    <Link
                      href="/trust-center"
                      onClick={onClose}
                      className={`flex-1 rounded-lg px-4 py-3 text-center font-medium transition-colors ${
                        darkMode
                          ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                    >
                      Visit Trust Center
                    </Link>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
